<template>
    <v-card flat class="pa-4" :loading="loading">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row dense>
          <!-- انتخاب استراتژی هوشمند قیمت‌گذاری -->
          <v-col cols="12">
            <div class="d-flex align-center mb-2">
              <v-icon color="primary" small class="ml-2">mdi-strategy</v-icon>
              <span class="text-body-2 font-weight-bold grey--text text--darken-2">استراتژی قیمت‌گذاری هوشمند</span>
            </div>
            <v-select
              v-model="form.pricing_strategy_id"
              :items="strategies"
              item-text="name"
              item-value="id"
              placeholder="انتخاب استراتژی (اختیاری)"
              clearable 
              outlined 
              dense
              hint="در صورت انتخاب، قیمت می‌تواند به‌طور خودکار محاسبه شود."
              persistent-hint
              class="bg-white"
            >
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon 
                      color="primary"
                      elevation="2"
                      class="bg-white mt-n1"
                      v-bind="attrs" 
                      v-on="on"
                      :disabled="!form.pricing_strategy_id"
                      :loading="applyingStrategy"
                      @click="applyStrategy"
                    >
                      <v-icon>mdi-calculator</v-icon>
                    </v-btn>
                  </template>
                  <span>محاسبه و اعمال قیمت بر اساس این استراتژی</span>
                </v-tooltip>
              </template>
            </v-select>
          </v-col>

          <v-divider class="my-4 w-100"></v-divider>

          <!-- فیلد قیمت نهایی -->
          <v-col cols="12" md="6">
            <price-input
              v-model="form.price"
              label="قیمت فروش در مارکت‌پلیس (ریال)"
              :rules="[rules.required, rules.minZero]"
              outlined 
              dense
              class="bg-white"
            />
          </v-col>

          <!-- درصد سود -->
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="form.profit_percent"
              label="درصد سود"
              type="number"
              suffix="%"
              prepend-inner-icon="mdi-chart-line"
              min="0"
              max="100"
              :rules="[rules.required, rules.percentRange]"
              outlined 
              dense
              class="bg-white"
            />
          </v-col>

          <!-- مبنای اعمال درصد سود -->
          <v-col cols="12" md="3">
            <v-select
              v-model="form.profit_target_price"
              :items="profitTargetOptions"
              item-text="text"
              item-value="value"
              label="مبنای اعمال سود"
              prepend-inner-icon="mdi-target"
              outlined 
              dense
              :rules="[rules.required]"
              class="bg-white"
            />
          </v-col>

          <!-- موجودی در مارکت‌پلیس -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="form.stock"
              label="موجودی اختصاصی"
              type="number"
              :rules="[rules.required, rules.minZero]"
              outlined 
              dense
              class="bg-white"
            />
          </v-col>

          <!-- هزینه ارسال -->
          <v-col cols="12" md="6">
            <price-input
              v-model="form.shipping_cost"
              label="هزینه ارسال به مشتری (ریال)"
              :rules="[rules.minZero]"
              outlined 
              dense
              class="bg-white"
            />
          </v-col>

          <!-- زمان ارسال -->
          <v-col cols="12" md="6">
            <v-text-field
              v-model.number="form.shipping_time"
              label="زمان آماده‌سازی و ارسال (ساعت)"
              type="number"
              :rules="[rules.minZero]"
              outlined 
              dense
              class="bg-white"
            />
          </v-col>
        </v-row>

        <div class="d-flex align-center mt-6 gap-2">
          <v-btn class="btn-delete rounded-lg px-4" @click="closeModal" :disabled="saving">
            <v-icon>mdi-close</v-icon>
            انصراف
          </v-btn>
          <v-spacer/>

          <v-btn 
            v-if="hasExistingRecord" 
            outlined 
            class="btn-delete rounded-lg" 
            @click="deleteSettings"
            :loading="deleting"
          >
            <v-icon right small>mdi-trash-can-outline</v-icon>
            حذف تنظیمات (بازگشت به پیش‌فرض)
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn class="btn-get-code rounded-lg px-6" :loading="saving" @click="savePricing">
            <v-icon right small>mdi-check</v-icon>
            ذخیره تغییرات
          </v-btn>
        </div>
      </v-form>
    </v-card>
</template>

<script>
import PriceInput from "../../../../core/plugins/TitleRibbon/pages/pickers/priceInput.vue";

export default {
  name: "MarketplacePricinModal",
  components: { PriceInput },
  props: {
    value: { type: Boolean, default: false },
    listingId: { type: [Number, String], default: 1 },
  },
  data() {
    return {
      valid: true,
      loading: false,
      saving: false,
      deleting: false,
      applyingStrategy: false,
      hasExistingRecord: false, 
      strategies: [],
      profitTargetOptions: [
        { text: "کف قیمت (حداقل)", value: "min" },
        { text: "سقف قیمت (حداکثر)", value: "max" },
      ],
      form: this.getInitialForm(),
      rules: {
        required: (v) => (v !== null && v !== "" && v !== undefined) || "این فیلد الزامی است.",
        minZero: (v) => {
          if (!v) return true;
          return Number(String(v).replace(/,/g, '')) >= 0 || "مقدار نمی‌تواند منفی باشد.";
        },
        percentRange: (v) => {
          if (v === null || v === "" || v === undefined) return true;
          const val = Number(v);
          return (val >= 0 && val <= 100) || "درصد سود باید بین ۰ تا ۱۰۰ باشد.";
        },
      },
    };
  },
  computed: {
    isOpen: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); }
    }
  },
  watch: {
    isOpen: {
      immediate: true, 
      handler(newVal) {
        if (newVal) {
          this.fetchInitialData();
        } else {
          setTimeout(() => {
            this.form = this.getInitialForm();
            this.hasExistingRecord = false;
            if (this.$refs.form) this.$refs.form.resetValidation();
          }, 300);
        }
      }
    },
  },
  methods: {
    getInitialForm() {
      return {
        price: 0,
        profit_percent: 0,
        profit_target_price: "min",
        stock: 0,
        shipping_cost: 0,
        shipping_time: 24, 
        pricing_strategy_id: null,
      };
    },

    closeModal() {
      this.isOpen = false;
    },

    async fetchInitialData() {
      this.loading = true;
      try {
        const response = await this.$api.get("/v1/smart-pricing-strategies?page=1&length=500000");
        this.strategies = response.data.data.data || response.data.data || response.data;
        const priceRes = await this.$api.get(`v1/marketplace-listings/${this.listingId}/pricing-setting`); 
        
        if (priceRes.data?.data) {
          const data = priceRes.data.data;
          this.hasExistingRecord = true;
          this.form = {
            price: data.price || 0,
            profit_percent: data.profit_percent !== undefined && data.profit_percent !== null 
              ? Number(data.profit_percent) 
              : 0,
            profit_target_price: data.profit_target_price || "min",
            stock: data.stock || 0,
            shipping_cost: data.shipping_cost || 0,
            shipping_time: data.shipping_time || 24,
            pricing_strategy_id: data.pricing_strategy_id || null,
          };
        } else {
          this.hasExistingRecord = false;
        }
      } catch (e) {
        this.hasExistingRecord = false;
        console.error("Error fetching initial data:", e);
      } finally {
        this.loading = false;
      }
    },

    async applyStrategy() {
      if (!this.form.pricing_strategy_id) return;
      
      this.applyingStrategy = true;
      try {
        const payload = { pricing_strategy_id: this.form.pricing_strategy_id };
        
        const { data } = await this.$api.post(
          `/v1/marketplace-pricing-shipping/${this.listingId}/apply-strategy`,
          payload
        );
        
        if (data.data?.calculated_price) {
          this.form.price = data.data.calculated_price;
          
          let msg = "قیمت با موفقیت محاسبه شد.";
          if (data.data.min_price || data.data.max_price) {
             msg += " (با در نظر گرفتن محدودیت‌های کف و سقف)";
          }
          this.$toast.success(msg);
        }
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در محاسبه قیمت از طریق استراتژی");
      } finally {
        this.applyingStrategy = false;
      }
    },

    async savePricing() {
      if (!this.$refs.form.validate()) return;
      
      this.saving = true;
      try {
        const payload = {
          price: Number(String(this.form.price).replace(/,/g, '')),
          profit_percent: Number(this.form.profit_percent || 0),
          profit_target_price: this.form.profit_target_price || "min",
          stock: Number(this.form.stock),
          shipping_cost: Number(String(this.form.shipping_cost).replace(/,/g, '')),
          shipping_time: Number(this.form.shipping_time),
          pricing_strategy_id: this.form.pricing_strategy_id,
        };

        await this.$api.post(`/v1/marketplace-pricing-shipping/${this.listingId}`, payload);
        
        this.$toast.success("تنظیمات اختصاصی با موفقیت اعمال شد.");
        this.$emit("saved");
        this.closeModal();
      } catch (e) {
        this.$toast.error(e.response?.data?.message || "خطا در ثبت اطلاعات");
      } finally {
        this.saving = false;
      }
    },

    async deleteSettings() {
      if (!confirm("آیا از حذف تنظیمات اختصاصی این لیستینگ مطمئن هستید؟ (محصول به حالت پیش‌فرض برمی‌گردد)")) return;
      
      this.deleting = true;
      try {
        await this.$api.delete(`/v1/marketplace-pricing-shipping/${this.listingId}`);
        this.$toast.success("تنظیمات اختصاصی حذف شد.");
        this.$emit("saved");
        this.closeModal();
      } catch (e) {
         this.$toast.error(e.response?.data?.message || "خطا در حذف تنظیمات");
      } finally {
        this.deleting = false;
      }
    }
  },
};
</script>

<style scoped>
.gap-2 { gap: 8px; }
.bg-white { background-color: #ffffff !important; }
</style>
